import React, { useMemo, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { useReviewMode } from '../../providers/review-mode-providers';
import { showClassificationActionPanelAtom } from '../atoms';
import { Classification } from '../classification';
import { CircleCheckIcon } from '../icons/circle-check-icon';
import { PanelStateRenderer } from '../panel-state-renderer';
import { PanelContainer } from '../styled-component-overrides';
import { ActionPanel } from './action-panel';
type Props = {
  annotationIds: string[];
  selectedAnnotationId?: string;
};
export const ClassificationPanel = ({
  annotationIds,
  selectedAnnotationId
}: Props) => {
  const showClassificationActionPanel = useAtomValue(showClassificationActionPanelAtom);
  const previousValidIndexRef = useRef(0);
  const {
    selectAnnotation
  } = useReviewMode();
  const currentAnnotationIndex = useMemo(() => {
    const index = annotationIds.findIndex(id => id === selectedAnnotationId);
    if (index !== -1) {
      // Found the annotation, update and store the valid index
      // eslint-disable-next-line react-hooks/refs
      previousValidIndexRef.current = index;
      return index;
    }
    // Not found, return the previous valid index (clamped to current array bounds)
    // eslint-disable-next-line react-hooks/refs
    const clampedIndex = Math.min(previousValidIndexRef.current, annotationIds.length - 1);
    return Math.max(0, clampedIndex);
  }, [annotationIds, selectedAnnotationId]);
  const onPreviousObjectClick = () => {
    if (currentAnnotationIndex > 0) {
      const previousAnnotationId = annotationIds[currentAnnotationIndex - 1];
      if (!previousAnnotationId) {
        return;
      }
      selectAnnotation?.(previousAnnotationId);
    }
  };
  const onNextObjectClick = () => {
    if (currentAnnotationIndex < annotationIds.length - 1) {
      const nextAnnotationId = annotationIds[currentAnnotationIndex + 1];
      if (!nextAnnotationId) {
        return;
      }
      selectAnnotation?.(nextAnnotationId);
    }
  };
  if (annotationIds.length === 0) {
    return <PanelStateRenderer icon={<CircleCheckIcon />} title="No annotations" description="No annotations to review" />;
  }
  return <PanelContainer style={{
    height: '100%'
  }}>
      <Classification annotationIds={annotationIds} currentAnnotationIndex={currentAnnotationIndex} selectedAnnotationId={selectedAnnotationId} />
      {showClassificationActionPanel && <ActionPanel disabledPreviousObject={currentAnnotationIndex === 0} disabledNextObject={currentAnnotationIndex === annotationIds.length - 1} onPreviousObjectClick={onPreviousObjectClick} onNextObjectClick={onNextObjectClick} />}
    </PanelContainer>;
};